/**
 * DMC-E — Canonical TS parking-lot helpers.
 *
 * The single source of truth for "this ticket is in the parking lot" is
 * `tickets.parking_lot` (boolean column). The legacy `status='Parking
 * Lot'` representation was deprecated and is now rejected by the
 * `tickets_status_check` CHECK constraint (migration
 * `20260424000000_timer_state_machine_invariants.sql`) — any pre-D
 * rows were force-converted to `'Deleted'` in that same migration.
 *
 * Canonical contract:
 *
 *   isInParkingLot(t)         ⟺  t.parking_lot === true
 *   isPhysicallyLocated(t)    ⟺  t.parking_lot === true
 *                                 || t.parking_spot_id != null
 *                                 || t.bay_id != null
 *
 * `isPhysicallyLocated` mirrors the SQL trigger
 * `public.sync_vehicle_present` (migration `20260309030431`) which
 * derives `tickets.vehicle_present` from the same triad. Reading
 * either `vehicle_present` (the trigger-maintained mirror column) OR
 * calling this helper produces equivalent results — the helper is
 * useful for callers that already have the row in memory and want to
 * avoid relying on the mirror column staying in sync (e.g., partial
 * row projections that don't fetch `vehicle_present`).
 *
 * Canonical write paths that maintain the triad's invariants:
 *   • `public.move_vehicle_location` SQL RPC (canonical user-initiated mover)
 *   • `mutateTicketTimerState` (timer transitions: stop → park, etc.)
 *   • `OptiPlaceModal`, `CreateTicket`, `ImportJobs` (initial creation)
 *
 * USAGE:
 *
 *   import { isInParkingLot, isPhysicallyLocated } from '@/utils/parkingLotState';
 *
 *   if (isInParkingLot(ticket)) { ... }
 *   const located = tickets.filter(isPhysicallyLocated);
 *
 * NOT YET BROADLY ADOPTED — DMC-E adopts the helper only at the one
 * site (`invariants.ts:checkParkingLotImmunity`) where the prior code
 * branched on the deprecated `status='Parking Lot'` value. All other
 * `parking_lot` reads in the codebase already check the canonical
 * boolean column directly and need no migration.
 */

/**
 * Minimal shape: anything with optional `parking_lot`, `parking_spot_id`,
 * and `bay_id` fields. Intentionally permissive so callers can pass
 * partial `Ticket` rows, snapshot rows, query-projection rows, etc.,
 * without structural-type widening at every call site.
 */
export interface ParkingLotStateTicketShape {
  parking_lot?: boolean | null;
  parking_spot_id?: string | null;
  bay_id?: string | null;
}

/**
 * Canonical "is this ticket in the parking lot" predicate.
 *
 * Returns `true` when `ticket.parking_lot === true`. Strict equality
 * to avoid coercing `null` / `undefined` / `0` to false-but-different.
 */
export function isInParkingLot(ticket: ParkingLotStateTicketShape): boolean {
  return ticket.parking_lot === true;
}

/**
 * Canonical "does this ticket have any PHYSICAL LOCATION" predicate.
 *
 * Returns `true` when ANY of the three physical-location reps are set:
 *   - `parking_lot === true`, OR
 *   - `parking_spot_id` is non-null, OR
 *   - `bay_id` is non-null.
 *
 * SCOPE: location-only.
 * --------------------
 * This helper covers the LOCATION TRIAD only (parking lot / parking
 * spot / bay). It does NOT include timer state.
 *
 * NOT a mirror of the deployed `sync_vehicle_present` trigger.
 * -----------------------------------------------------------
 * The currently-deployed trigger (migration `20260412222311`) computes
 * `vehicle_present` from FOUR inputs:
 *
 *   parking_lot OR parking_spot_id OR bay_id OR timer_status IN ('running','paused')
 *
 * `isPhysicallyLocated` deliberately omits the timer arm — it answers
 * "is this ticket physically somewhere?", not "would the DB column be
 * true?". An earlier version of the trigger (migration
 * `20260309030431`) used the same 3-input shape this helper has, but
 * that version was superseded; this helper's doc-block is intentionally
 * NOT updated to match the new trigger. The two predicates are
 * structurally distinct.
 *
 * Callers needing DB-column parity (i.e., the value
 * `tickets.vehicle_present` carries after the trigger runs) MUST use
 * `isVehiclePresent` from `@/utils/deriveVehiclePresence` instead —
 * that helper includes the timer arm and exactly mirrors the deployed
 * trigger.
 *
 *   isPhysicallyLocated(t)  ⇒  location triad only (this helper)
 *   isVehiclePresent(t)     ⇒  DB-column parity (location triad + timer)
 *
 * Drift-prevention: any future change to the deployed
 * `sync_vehicle_present` trigger MUST be propagated to
 * `isVehiclePresent`, NOT to this helper. The dual-rep alignment test
 * `dualRep1VehiclePresentDoctrineAlignment` enforces this.
 */
export function isPhysicallyLocated(ticket: ParkingLotStateTicketShape): boolean {
  return (
    ticket.parking_lot === true ||
    ticket.parking_spot_id != null ||
    ticket.bay_id != null
  );
}
